import { useEffect, useState } from "react";
import { api, formatErr } from "../lib/api";
import { toast } from "sonner";
import { Broadcast, DownloadSimple, Trash, Clock } from "@phosphor-icons/react";

function fmtDuration(sec) {
    if (!sec) return "—";
    const h = Math.floor(sec / 3600);
    const m = Math.floor((sec % 3600) / 60);
    const s = sec % 60;
    return h > 0 ? `${h}h ${m}m` : m > 0 ? `${m}m ${s}s` : `${s}s`;
}

function fmtRemaining(expiresAt) {
    const ms = new Date(expiresAt).getTime() - Date.now();
    if (ms <= 0) return "Expiring";
    const h = Math.floor(ms / 3600000);
    const m = Math.floor((ms % 3600000) / 60000);
    return h > 0 ? `${h}h ${m}m left` : `${m}m left`;
}

export default function LiveArchive() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        try {
            const { data } = await api.get("/live/archive/me");
            setItems(data);
        } catch (e) {
            toast.error(formatErr(e.response?.data?.detail));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        const t = setInterval(() => setItems((s) => [...s]), 60_000); // tick countdown every min
        return () => clearInterval(t);
    }, []);

    const onDownload = async (id) => {
        try {
            const { data } = await api.get(`/live/archive/${id}/download`);
            // Create a hidden anchor with download attribute (cross-origin download depends on remote CORS,
            // but with download attribute set, browser will at least attempt to save).
            const a = document.createElement("a");
            a.href = data.download_url;
            a.download = data.filename || "live-stream.mp4";
            a.target = "_blank";
            a.rel = "noopener";
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            toast.success("Download started");
        } catch (e) {
            toast.error(formatErr(e.response?.data?.detail));
        }
    };

    const onDelete = async (id) => {
        if (!window.confirm("Delete this recording? This is permanent.")) return;
        try {
            await api.delete(`/live/archive/${id}`);
            setItems((arr) => arr.filter((a) => a.id !== id));
            toast.success("Removed");
        } catch (e) {
            toast.error(formatErr(e.response?.data?.detail));
        }
    };

    if (loading) return <div className="text-wta-muted text-sm" data-testid="live-archive-loading">Loading archive…</div>;

    return (
        <div data-testid="live-archive-panel" className="bg-wta-surface border border-wta-border rounded-sm p-6">
            <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
                <div className="flex items-center gap-2">
                    <Broadcast size={20} weight="fill" className="text-wta-primary" />
                    <div className="font-heading text-2xl tracking-wider uppercase">Live Archive</div>
                </div>
                <div className="text-xs text-wta-muted flex items-center gap-1">
                    <Clock size={12} /> Auto-deleted 48 hours after stream ends
                </div>
            </div>

            {items.length === 0 ? (
                <div data-testid="live-archive-empty" className="text-wta-muted text-sm">
                    No archived live streams. Recordings appear here after you end a live broadcast.
                </div>
            ) : (
                <div className="space-y-2">
                    {items.map((a) => {
                        const expiringSoon = (new Date(a.expires_at).getTime() - Date.now()) < 6 * 3600 * 1000;
                        return (
                            <div
                                key={a.id}
                                data-testid={`archive-row-${a.id}`}
                                className="flex items-center gap-3 bg-wta-surface2 border border-wta-border rounded-sm p-3"
                            >
                                <div className="w-20 aspect-video bg-black rounded-sm overflow-hidden flex items-center justify-center shrink-0">
                                    {a.thumbnail
                                        ? <img src={a.thumbnail} alt="" className="w-full h-full object-cover" />
                                        : <Broadcast size={20} className="text-white/40" weight="fill" />}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="font-heading text-sm tracking-wider truncate">
                                        Live · {new Date(a.started_at || a.created_at).toLocaleString()}
                                    </div>
                                    <div className="text-xs text-wta-muted flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5">
                                        <span>Duration {fmtDuration(a.duration_sec)}</span>
                                        <span className={expiringSoon ? "text-wta-primary" : ""}>{fmtRemaining(a.expires_at)}</span>
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    data-testid={`archive-download-${a.id}`}
                                    onClick={() => onDownload(a.id)}
                                    className="btn-primary px-3 py-2 rounded-sm text-xs flex items-center gap-1"
                                >
                                    <DownloadSimple size={14} weight="bold" /> Download
                                </button>
                                <button
                                    type="button"
                                    data-testid={`archive-delete-${a.id}`}
                                    onClick={() => onDelete(a.id)}
                                    className="btn-ghost px-2.5 py-2 rounded-sm text-xs"
                                    aria-label="Delete recording"
                                >
                                    <Trash size={14} />
                                </button>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
